import { z } from 'zod';

/**
 * Dance Battle (spec §6.14.5): a rhythm game for 1–4 players on the dance floor. The server picks
 * a seed and a start time; every client builds the same chart from the seed and plays it on the
 * synced server clock. Hits are judged on the client for instant feedback and judged again by
 * the server, which keeps the score. No audio: the beat is shown, not heard.
 */

export const DANCE_MAX_PLAYERS = 4;
export const DANCE_LANES = 4;
/** Countdown between "start" and the first beat of the song. */
export const DANCE_COUNTDOWN_MS = 3_000;
/** Silence before the first note, so players can find the keys. */
export const DANCE_LEAD_IN_MS = 2_000;
export const DANCE_BEAT_MS = 500;
export const DANCE_BEATS = 56;
/** How long the result screen stays before the floor opens for the next round. */
export const DANCE_RESULTS_MS = 8_000;
/** Hit windows (spec: ±120 ms). */
export const DANCE_PERFECT_MS = 50;
export const DANCE_GOOD_MS = 120;
export const DANCE_POINTS = { perfect: 100, good: 50 } as const;
/** Bonus per hit for the running combo, capped. */
export const DANCE_COMBO_BONUS = 2;
export const DANCE_COMBO_CAP = 30;
/** A press with no note in reach costs points and breaks the combo (stops key mashing). */
export const DANCE_STRAY_PENALTY = 30;
export const DANCE_MAX_NOTES = 96;
/** Clients send their presses in batches; one batch carries at most this many. */
export const DANCE_MAX_HITS_PER_BATCH = 16;
/** Accepted age of a press when its batch arrives (batching delay plus network). */
export const DANCE_HIT_MAX_AGE_MS = 1_200;
/** Accepted lead of a press over the server clock (client clock error). */
export const DANCE_HIT_MAX_LEAD_MS = 250;

export type DanceGrade = 'perfect' | 'good';

export interface DanceNote {
  /** Index in the chart (bit in `judged`). */
  readonly index: number;
  readonly lane: number;
  /** Time from the start of the song (`startAt`), ms. */
  readonly t: number;
}

export interface DancePlayer {
  /** Session id. */
  readonly id: string;
  readonly name: string;
  readonly score: number;
  readonly combo: number;
  readonly best: number;
  readonly perfect: number;
  readonly good: number;
  /** Notes this player has hit, as a hex bit set (see `isJudged`). */
  readonly judged: string;
}

/** The dance floor's shared state (synced object data). */
export interface DanceBattleData {
  readonly game: 'dance';
  readonly phase: 'lobby' | 'playing' | 'results';
  readonly players: readonly DancePlayer[];
  /** Chart seed of the current or last round. */
  readonly seed: number;
  /** Server time the song starts (after the countdown). */
  readonly startAt: number;
  /** Server time the round ends. */
  readonly endAt: number;
  /** Server time the result screen closes. */
  readonly resultsUntil: number;
}

export const danceHitSchema = z.object({
  hits: z
    .array(
      z
        .object({
          lane: z
            .number()
            .int()
            .min(0)
            .max(DANCE_LANES - 1),
          t: z.number().finite(),
        })
        .strict(),
    )
    .min(1)
    .max(DANCE_MAX_HITS_PER_BATCH),
});
export type DanceHitInput = z.infer<typeof danceHitSchema>;

/** Small deterministic PRNG (mulberry32): same seed, same chart on every machine. */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/**
 * The note chart for a seed: a note on most beats, off-beats appearing as the song goes on,
 * never two notes at the same moment.
 */
export function danceChart(seed: number): readonly DanceNote[] {
  const random = seededRandom(seed);
  const notes: DanceNote[] = [];
  let lastLane = -1;
  const add = (t: number) => {
    let lane = Math.floor(random() * DANCE_LANES);
    if (lane === lastLane && random() < 0.6)
      lane = (lane + 1 + Math.floor(random() * (DANCE_LANES - 1))) % DANCE_LANES;
    lastLane = lane;
    notes.push({ index: notes.length, lane, t });
  };
  for (let beat = 0; beat < DANCE_BEATS && notes.length < DANCE_MAX_NOTES; beat++) {
    const t = DANCE_LEAD_IN_MS + beat * DANCE_BEAT_MS;
    const progress = beat / DANCE_BEATS;
    if (random() < 0.7 + progress * 0.25) add(t);
    if (notes.length < DANCE_MAX_NOTES && beat >= 8 && random() < progress * 0.45) {
      add(t + DANCE_BEAT_MS / 2);
    }
  }
  return notes;
}

/** Length of the song: the last note plus a short tail. */
export function danceSongLength(chart: readonly DanceNote[]): number {
  const last = chart[chart.length - 1];
  return (last ? last.t : DANCE_LEAD_IN_MS) + 1_500;
}

export function isJudged(judged: string, index: number): boolean {
  const digit = Number.parseInt(judged.charAt(index >> 2) || '0', 16);
  return (digit & (1 << (index & 3))) !== 0;
}

export function markJudged(judged: string, index: number): string {
  const position = index >> 2;
  const padded = judged.padEnd(position + 1, '0');
  const digit = Number.parseInt(padded.charAt(position), 16) | (1 << (index & 3));
  return padded.slice(0, position) + digit.toString(16) + padded.slice(position + 1);
}

export interface DanceJudgement {
  readonly note: DanceNote;
  readonly grade: DanceGrade;
}

/**
 * Judges a press at `songTime` (ms since `startAt`) in a lane: the closest note in that lane
 * within the good window that this player has not hit yet, or `undefined` for a stray press.
 */
export function judgeDanceHit(
  chart: readonly DanceNote[],
  judged: string,
  lane: number,
  songTime: number,
): DanceJudgement | undefined {
  let best: DanceNote | undefined;
  for (const note of chart) {
    if (note.lane !== lane || isJudged(judged, note.index)) continue;
    const offset = Math.abs(note.t - songTime);
    if (offset > DANCE_GOOD_MS) continue;
    if (!best || offset < Math.abs(best.t - songTime)) best = note;
  }
  if (!best) return undefined;
  const grade = Math.abs(best.t - songTime) <= DANCE_PERFECT_MS ? 'perfect' : 'good';
  return { note: best, grade };
}

/**
 * A player's state after one press. A stray press costs points and breaks the combo; a hit
 * scores, and continues the combo only when no earlier note slipped past unhit.
 */
export function applyDanceHit(
  chart: readonly DanceNote[],
  player: DancePlayer,
  judgement: DanceJudgement | undefined,
): DancePlayer {
  if (!judgement) {
    return { ...player, score: Math.max(0, player.score - DANCE_STRAY_PENALTY), combo: 0 };
  }
  const missedBefore = chart.some(
    (note) => note.t < judgement.note.t && !isJudged(player.judged, note.index),
  );
  const running = missedBefore ? 0 : player.combo;
  const bonus = Math.min(running, DANCE_COMBO_CAP) * DANCE_COMBO_BONUS;
  const combo = running + 1;
  return {
    ...player,
    score: player.score + DANCE_POINTS[judgement.grade] + bonus,
    combo,
    best: Math.max(player.best, combo),
    perfect: player.perfect + (judgement.grade === 'perfect' ? 1 : 0),
    good: player.good + (judgement.grade === 'good' ? 1 : 0),
    judged: markJudged(player.judged, judgement.note.index),
  };
}

export function newDancePlayer(id: string, name: string): DancePlayer {
  return { id, name, score: 0, combo: 0, best: 0, perfect: 0, good: 0, judged: '' };
}

/** Players ranked by score (then best combo, then join order). */
export function danceRanking(players: readonly DancePlayer[]): DancePlayer[] {
  return players
    .map((player, order) => ({ player, order }))
    .sort(
      (a, b) =>
        b.player.score - a.player.score || b.player.best - a.player.best || a.order - b.order,
    )
    .map(({ player }) => player);
}
